// Shared password-strength rules, used by BOTH the frontend (signup / reset
// password UI) and the backend (registerUser / reset endpoints) so the policy
// lives in exactly one place. Dependency-free and safe to run in the browser or
// in Node.
//
// Policy (standard): at least 8 characters, and at least one uppercase letter,
// one lowercase letter, one number, and one special character.

export interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

export interface PasswordRuleResult {
  key: string;
  label: string;
  met: boolean;
}

export interface PasswordValidationResult {
  valid: boolean;
  results: PasswordRuleResult[];
  errors: string[];
}

export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'min8', label: 'At least 8 characters', test: (p) => (p || '').length >= 8 },
  { key: 'upper', label: 'An uppercase letter (A–Z)', test: (p) => /[A-Z]/.test(p || '') },
  { key: 'lower', label: 'A lowercase letter (a–z)', test: (p) => /[a-z]/.test(p || '') },
  { key: 'number', label: 'A number (0–9)', test: (p) => /[0-9]/.test(p || '') },
  { key: 'special', label: 'A special character (e.g. !@#$%)', test: (p) => /[^A-Za-z0-9]/.test(p || '') },
];

export const PASSWORD_REQUIREMENTS_TEXT =
  'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character.';

// Evaluate a password against every rule. `results` is safe to render as a
// live checklist; `valid` is the gate for enabling submit / passing the API check.
export function validatePassword(password: string): PasswordValidationResult {
  const pw = password || '';
  const results: PasswordRuleResult[] = PASSWORD_RULES.map((rule) => ({
    key: rule.key,
    label: rule.label,
    met: rule.test(pw),
  }));
  const errors = results.filter((r) => !r.met).map((r) => r.label);
  return { valid: errors.length === 0, results, errors };
}

export default validatePassword;
